import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { FileCategory } from "../../../Core/Api/Types/file-category";
import { cacheService, fileService, mediaService } from "../../../Core/Api/services";
import { checkAndDownloadFile, DownloadFileFromUrl } from "../../../Utils/helpers";
import { ChevronRightOutlined, FileDownloadOutlined, HighQualityOutlined, SdOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useRef, useState } from "react";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { FileStatus } from "../../../Core/Api/Types/file-status";
import { ModalContext } from "../../Modals";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { FileAccessibility } from "../../../Core/Api/Types/file-accessibility";
import ModalFileOfflineConfirmAddToBasket, {
  ModalFileOfflineConfirmAddToBasketProps,
} from "../../Modals/ModalFileOfflineConfirmAddToBasket/modal-file-offline-confirm-add-to-basket";

interface IDownloadMenuItemProps {
  canDownload: boolean;
  obj: DbAssetObjectInfos;
  handleMoreClose: () => void;
}

export const DownloadMenuItem: React.FC<IDownloadMenuItemProps> = (props) => {
  const { canDownload, obj, handleMoreClose } = props;
  const { t } = useTranslation();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { addObjectByIdToBasket } = useBasketActions();
  const menuRef = useRef(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [anchorSubMenuEl, setAnchorSubMenuEl] = useState<null | HTMLElement>(null);
  const subMenuOpen = Boolean(anchorSubMenuEl);
  const handleSubMenuOpen = (event: React.MouseEvent<HTMLLIElement>) => {
    setAnchorSubMenuEl(event.currentTarget);
  };
  const handleSubMenuClose = () => {
    setAnchorSubMenuEl(null);
    timerRef.current = setTimeout(() => setAnchorSubMenuEl(null), 200);
  };

  function handleDownload(e: React.MouseEvent, category: FileCategory) {
    e.stopPropagation();

    if (obj!.type === ObjectType.Folder) {
      const response = fileService.DownloadHttpZipFile({
        objectIds: [obj!.id],
        includeAllChilds: true,
        proxy: false,
        skipFileIfOffline: true,
        password: "",
        authKey: cacheService.GetAuthKey()!,
      });
      DownloadFileFromUrl(response, "");
    } else {
      if (category === FileCategory.Native) {
        // Check if file is online/disk
        mediaService.GetFilesByObjectId(obj.id).then((response) => {
          if (
            response.files?.find(
              (x) =>
                x.category === category &&
                x.status === FileStatus.Online &&
                (x.accessibility === FileAccessibility.Disk || x.accessibility === FileAccessibility.DiskAndTape)
            ) === undefined
          ) {
            if (!openModal) return;

            openModal<ModalFileOfflineConfirmAddToBasketProps>(ModalFileOfflineConfirmAddToBasket, {
              closeModal: () => {},
              onConfirm: () => {
                addObjectByIdToBasket(obj.id);
              },
            });

            handleMoreClose();
          } else startDownload(category);
        });
      } else {
        startDownload(category);
      }
    }
  }

  const startDownload = (category: FileCategory) => {
    enqueueCustomSnackbar(t("common:downloadStarted"), "primary");
    checkAndDownloadFile(
      mediaService.DownloadMediaFileByCategory(obj!.id, category, cacheService.GetAuthKey()!),
      ""
    ).then((resp) => {
      if (!resp.success) {
        enqueueCustomSnackbar(resp.errorMessage, "error");
      }
    });

    handleMoreClose();
  };

  return (
    <>
      {canDownload && obj!.type !== ObjectType.Video && (
        <MenuItem onClick={(e) => handleDownload(e, FileCategory.Native)}>
          <ListItemIcon>
            <FileDownloadOutlined fontSize="small" />
          </ListItemIcon>
          {t("common:download")}
        </MenuItem>
      )}
      {canDownload && obj!.type === ObjectType.Video && (
        <MenuItem
          onMouseEnter={handleSubMenuOpen}
          onMouseLeave={handleSubMenuClose}
          sx={(t) => ({ cursor: "pointer", background: subMenuOpen ? t.palette.backgroundAlpha.hover : "transparent" })}
          ref={menuRef}
        >
          <ListItemIcon>
            <FileDownloadOutlined fontSize="small" />
          </ListItemIcon>
          <Typography sx={{ flexGrow: 1 }}>{t("common:download")}</Typography>
          <ChevronRightOutlined sx={(t) => ({ color: t.palette.text.secondary })} fontSize="small" />
          <Menu
            open={subMenuOpen}
            anchorEl={anchorSubMenuEl}
            onClose={handleSubMenuClose}
            onClick={handleSubMenuClose}
            onMouseLeave={handleSubMenuClose}
            onMouseEnter={(e) => {
              clearTimeout(timerRef.current!);
            }}
            container={menuRef.current}
            sx={{
              top: "-12px",
              left: "4px",
              zIndex: 1,
              pointerEvents: "none",
              ".MuiPaper-root": { pointerEvents: "all" },
            }}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <MenuItem onClick={(e) => handleDownload(e, FileCategory.Native)}>
              <ListItemIcon>
                <HighQualityOutlined fontSize="small" />
              </ListItemIcon>
              {t("menu:item.downloadNativeSource")}
            </MenuItem>
            <MenuItem
              onClick={(e) => handleDownload(e, FileCategory.Proxy)}
              sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider} !important` })}
            >
              <ListItemIcon>
                <SdOutlined fontSize="small" />
              </ListItemIcon>
              {t("menu:item.downloadLowResolution")}
            </MenuItem>
          </Menu>
        </MenuItem>
      )}
    </>
  );
};

interface IMultiDownloadMenuItemProps {
  canDownload: boolean;
  objs: DbAssetObjectInfos[];
  handleMoreClose: () => void;
}

export const MultiDownloadMenuItem: React.FC<IMultiDownloadMenuItemProps> = (props) => {
  const { objs, canDownload, handleMoreClose } = props;
  const { t } = useTranslation();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleDownload = useCallback(() => {
    const itemsId = objs.reduce((acc, item) => [...acc, item.id], [] as number[]);
    const response = fileService.DownloadHttpZipFile({
      objectIds: itemsId,
      includeAllChilds: true,
      proxy: false,
      skipFileIfOffline: true,
      password: "",
      authKey: cacheService.GetAuthKey()!,
    });
    DownloadFileFromUrl(response, "");
    enqueueCustomSnackbar(t("common:downloadStarted"), "primary");
    handleMoreClose();
  }, [objs]);

  if (!canDownload) return <></>;
  return (
    <MenuItem onClick={handleDownload} disabled={objs.length > GlobalConst.MAX_DRIVE_DOWNLOADABLE_FILES}>
      <ListItemIcon>
        <FileDownloadOutlined />
      </ListItemIcon>
      <ListItemText>
        {objs.length > GlobalConst.MAX_DRIVE_DOWNLOADABLE_FILES
          ? t("menu:item.selectionTooLarge", { count: GlobalConst.MAX_DRIVE_DOWNLOADABLE_FILES })
          : t("button:downloadAll")}
      </ListItemText>
    </MenuItem>
  );
};
